
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  createStripeAccountLink, 
  getStripeConnectAccount, 
  disconnectStripeAccount, 
  getStripeAccountStatus,
  StripeAccountStatus
} from '@/lib/stripeConnect';
import { useUserProfile } from '@/hooks/useUserProfile';
import { Loader2, CreditCard, AlertCircle, CheckCircle, RefreshCcw, UnlinkIcon, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { isProfileData } from '@/lib/rpcFunctions';

export const PaymentsTab: React.FC = () => {
  const { profile, isLoading: profileLoading } = useUserProfile();
  const [stripeAccount, setStripeAccount] = useState<any>(null);
  const [accountStatus, setAccountStatus] = useState<StripeAccountStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stripeStatus = params.get('stripe_status');
    
    if (stripeStatus) {
      navigate(location.pathname, { replace: true });
      
      if (stripeStatus === 'success') {
        toast.success('Stripe account successfully connected!');
        refreshStripeAccountStatus();
      } else if (stripeStatus === 'refresh') {
        toast.info('Stripe onboarding was interrupted. Please continue the process.');
        setIsReconnecting(true);
      }
    }
  }, [location, navigate]);

  const fetchStripeAccount = async () => {
    if (profile && isProfileData(profile)) {
      setFetchError(null);
      try {
        console.log('Fetching Stripe account for user:', profile.id);
        
        // Fetch both the account record and the updated status from Stripe API
        const [account, status] = await Promise.all([
          getStripeConnectAccount(profile.id),
          getStripeAccountStatus(profile.id)
        ]);
        
        console.log('Stripe account retrieved:', account);
        console.log('Stripe account status:', status);
        
        setStripeAccount(account);
        setAccountStatus(status);
        setIsReconnecting(false);
      } catch (error) {
        console.error('Error fetching Stripe account:', error);
        setFetchError('Unable to connect to payment services. Please try again later.');
        toast.error('Failed to retrieve Stripe account information');
      }
    }
  };

  useEffect(() => {
    if (profile && isProfileData(profile)) {
      fetchStripeAccount();
    }
  }, [profile]);

  const refreshStripeAccountStatus = () => {
    setIsLoading(true);
    fetchStripeAccount()
      .finally(() => setIsLoading(false));
  };

  const handleConnectStripe = async () => {
    if (!profile || !isProfileData(profile)) {
      toast.error('User profile not found');
      return;
    }

    setIsLoading(true);
    try {
      const accountLinkUrl = await createStripeAccountLink(profile.id);
      window.location.href = accountLinkUrl;
    } catch (error) {
      console.error('Error creating Stripe account link:', error);
      toast.error('Failed to create Stripe account link. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!profile || !isProfileData(profile)) return;
    
    const confirmed = window.confirm('Are you sure you want to disconnect your Stripe account? This will prevent you from receiving payments.');
    if (!confirmed) return;
    
    setIsLoading(true);
    try {
      const success = await disconnectStripeAccount(profile.id);
      if (success) {
        await fetchStripeAccount();
        toast.success('Stripe account successfully disconnected');
      }
    } catch (error) {
      console.error('Error disconnecting Stripe account:', error);
      toast.error('Failed to disconnect Stripe account');
    } finally {
      setIsLoading(false);
    }
  };

  if (profileLoading) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center h-64">
          <Loader2 className="animate-spin h-8 w-8" />
        </CardContent>
      </Card>
    );
  }

  // Check account status from the Stripe API response
  const isAccountConnected = accountStatus?.connected || false;
  const isOnboardingComplete = accountStatus?.onboardingComplete || false;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Payment Setup</CardTitle>
        <CardDescription>
          Connect your Stripe account to receive payouts from course sales.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {fetchError && (
          <Alert className="mb-4" variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Connection Error</AlertTitle>
            <AlertDescription>
              {fetchError}
            </AlertDescription>
          </Alert>
        )}
        
        {isReconnecting && (
          <Alert className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Onboarding Interrupted</AlertTitle>
            <AlertDescription>
              Your Stripe onboarding process was interrupted. Please continue the process.
            </AlertDescription>
          </Alert>
        )}

        {isAccountConnected ? (
          <div className="space-y-4">
            <div className={cn(
              "flex items-center justify-between p-4 rounded-md",
              isOnboardingComplete ? "bg-green-50 dark:bg-green-950" : "bg-yellow-50 dark:bg-yellow-950"
            )}>
              <div className="flex items-center gap-2">
                {isOnboardingComplete ? (
                  <CheckCircle className="text-green-500 h-5 w-5" />
                ) : (
                  <AlertCircle className="text-yellow-500 h-5 w-5" />
                )}
                <div>
                  <p className="text-sm font-medium">
                    {isOnboardingComplete 
                      ? 'Stripe Account Connected' 
                      : 'Stripe Account Setup Incomplete'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {isOnboardingComplete
                      ? 'Your Stripe account is set up and ready to receive payments.'
                      : 'Please complete the Stripe onboarding process to receive payments.'}
                  </p>
                  {accountStatus && !isOnboardingComplete && (
                    <div className="mt-1 text-xs">
                      <div>Details submitted: {accountStatus.detailsSubmitted ? '✅' : '❌'}</div>
                      <div>Charges enabled: {accountStatus.chargesEnabled ? '✅' : '❌'}</div>
                      <div>Payouts enabled: {accountStatus.payoutsEnabled ? '✅' : '❌'}</div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                {!isOnboardingComplete && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleConnectStripe}
                    disabled={isLoading}
                    className="gap-2"
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ExternalLink className="h-4 w-4" />
                    )}
                    Complete Setup
                  </Button>
                )}
                <Button 
                  variant="secondary" 
                  size="sm" 
                  onClick={refreshStripeAccountStatus} 
                  disabled={isLoading}
                >
                  {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDisconnect}
                  disabled={isLoading}
                  className="gap-2"
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <UnlinkIcon className="h-4 w-4" />
                  )}
                  Disconnect
                </Button>
              </div>
            </div>
            
            <div className="text-sm text-muted-foreground">
              <p>Important: Make sure you've completed these steps in your Stripe account:</p>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Verified your identity</li>
                <li>Added your banking information</li>
                <li>Completed all required Stripe account details</li>
              </ul>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <Button 
              onClick={handleConnectStripe} 
              disabled={isLoading}
              className="w-full"
            >
              {isLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CreditCard className="mr-2 h-4 w-4" />
              )}
              {isReconnecting ? 'Continue Stripe Setup' : 'Connect Stripe Account'}
            </Button>
            
            <div className="text-sm text-muted-foreground">
              <p>What happens next:</p>
              <ol className="list-decimal pl-5 mt-2 space-y-1">
                <li>You'll be redirected to Stripe to set up your account</li>
                <li>You'll need to provide business and banking information</li>
                <li>Once completed, you'll be redirected back to this page</li>
              </ol>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
